// Reusable sectioned form editor — used by both the global Content CMS
// (Admin → Content → BUY SIDE → Intake Forms) and the per-buyer override
// editor (Admin → client → BUYING side → Intake Forms). Edits an ordered
// list of sections, each with a title and a list of typed question fields.
import React from "react";
import { Plus, Trash2, ChevronUp, ChevronDown, RotateCcw } from "lucide-react";

export const FIELD_TYPES = [
  { value: "text", label: "Short text" },
  { value: "textarea", label: "Paragraph" },
  { value: "select", label: "Dropdown" },
  { value: "number", label: "Number" },
  { value: "tel", label: "Phone" },
];

export function slugifyKey(label, prefix = "field") {
  return (label || "").toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40)
    || `${prefix}_${Date.now().toString(36).slice(-4)}`;
}

export function SectionedFormEditor({ formKey, title, intro, sections, onChange, onReset, resetLabel = "Reset to defaults" }) {
  const setSections = (next) => onChange(next);
  const updateSection = (si, patch) => setSections(sections.map((s, j) => j === si ? { ...s, ...patch } : s));
  const removeSection = (si) => setSections(sections.filter((_, j) => j !== si));
  const moveSection = (si, dir) => {
    const j = si + dir; if (j < 0 || j >= sections.length) return;
    const next = [...sections]; [next[si], next[j]] = [next[j], next[si]]; setSections(next);
  };
  const addSection = () => setSections([...sections, { title: "New section", fields: [] }]);

  const setFields = (si, fields) => updateSection(si, { fields });
  const updateField = (si, fi, patch) => setFields(si, (sections[si].fields || []).map((f, j) => j === fi ? { ...f, ...patch } : f));
  const removeField = (si, fi) => setFields(si, (sections[si].fields || []).filter((_, j) => j !== fi));
  const moveField = (si, fi, dir) => {
    const arr = [...(sections[si].fields || [])]; const j = fi + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[fi], arr[j]] = [arr[j], arr[fi]]; setFields(si, arr);
  };
  const addField = (si) => setFields(si, [...(sections[si].fields || []), { key: slugifyKey("new question"), label: "New question", type: "text", placeholder: "" }]);

  return (
    <div className="card" style={{ padding: 28 }} data-testid={`intake-editor-${formKey}`}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <div>
          <h2 className="font-serif" style={{ fontSize: 24, fontWeight: 500, margin: 0 }}>{title}</h2>
          <p style={{ color: "var(--text-muted)", fontSize: 14, margin: "4px 0 0", maxWidth: 640 }}>{intro}</p>
        </div>
        {onReset && (
          <button type="button" data-testid={`intake-reset-${formKey}`} onClick={onReset} className="btn-ghost" style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6, color: "var(--text-muted)" }}>
            <RotateCcw size={13} /> {resetLabel}
          </button>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        {sections.map((sec, si) => (
          <div key={si} data-testid={`intake-${formKey}-section-${si}`} style={{ border: "1px solid var(--border)", borderRadius: 12, padding: 16, background: "#FBFAF6" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, fontWeight: 500, color: "var(--text-muted)", display: "block", marginBottom: 4 }}>Section header</label>
                <input data-testid={`intake-${formKey}-section-title-${si}`} className="input-field" value={sec.title || ""} onChange={(e) => updateSection(si, { title: e.target.value })} placeholder="e.g. How to reach you" />
              </div>
              <button type="button" className="btn-ghost" style={{ padding: 8 }} onClick={() => moveSection(si, -1)} disabled={si === 0} data-testid={`intake-${formKey}-section-up-${si}`}><ChevronUp size={14} /></button>
              <button type="button" className="btn-ghost" style={{ padding: 8 }} onClick={() => moveSection(si, +1)} disabled={si === sections.length - 1} data-testid={`intake-${formKey}-section-down-${si}`}><ChevronDown size={14} /></button>
              <button type="button" className="btn-ghost" style={{ padding: 8, color: "var(--secondary)" }} onClick={() => removeSection(si)} data-testid={`intake-${formKey}-section-remove-${si}`}><Trash2 size={14} /></button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {(sec.fields || []).map((f, fi) => (
                <div key={fi} data-testid={`intake-${formKey}-field-${si}-${fi}`} style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, padding: 12 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1.6fr) 130px auto", gap: 10, alignItems: "end" }}>
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 500, color: "var(--text-muted)", display: "block", marginBottom: 4 }}>Question</label>
                      <input
                        data-testid={`intake-${formKey}-field-label-${si}-${fi}`}
                        className="input-field"
                        value={f.label || ""}
                        onChange={(e) => {
                          const newLabel = e.target.value;
                          const shouldAutoKey = (f.label === "New question" || !f.key);
                          updateField(si, fi, { label: newLabel, ...(shouldAutoKey ? { key: slugifyKey(newLabel) } : {}) });
                        }}
                        placeholder="e.g. Best phone number"
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 500, color: "var(--text-muted)", display: "block", marginBottom: 4 }}>Type</label>
                      <select data-testid={`intake-${formKey}-field-type-${si}-${fi}`} className="input-field" value={f.type || "text"} onChange={(e) => updateField(si, fi, { type: e.target.value })}>
                        {FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button type="button" className="btn-ghost" style={{ padding: 8 }} onClick={() => moveField(si, fi, -1)} disabled={fi === 0} data-testid={`intake-${formKey}-field-up-${si}-${fi}`}><ChevronUp size={14} /></button>
                      <button type="button" className="btn-ghost" style={{ padding: 8 }} onClick={() => moveField(si, fi, +1)} disabled={fi === (sec.fields.length - 1)} data-testid={`intake-${formKey}-field-down-${si}-${fi}`}><ChevronDown size={14} /></button>
                      <button type="button" className="btn-ghost" style={{ padding: 8, color: "var(--secondary)" }} onClick={() => removeField(si, fi)} data-testid={`intake-${formKey}-field-remove-${si}-${fi}`}><Trash2 size={14} /></button>
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: f.type === "select" ? "1fr 1fr" : "1fr", gap: 10, marginTop: 10 }}>
                    <input
                      data-testid={`intake-${formKey}-field-placeholder-${si}-${fi}`}
                      className="input-field"
                      value={f.placeholder || ""}
                      onChange={(e) => updateField(si, fi, { placeholder: e.target.value })}
                      placeholder="Placeholder / example text (optional)"
                    />
                    {f.type === "select" && (
                      <input
                        data-testid={`intake-${formKey}-field-options-${si}-${fi}`}
                        className="input-field"
                        value={(f.options || []).join(", ")}
                        onChange={(e) => updateField(si, fi, { options: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })}
                        placeholder="Dropdown choices, comma-separated"
                      />
                    )}
                  </div>
                </div>
              ))}
              <button type="button" className="btn-ghost" onClick={() => addField(si)} data-testid={`intake-${formKey}-add-field-${si}`} style={{ fontSize: 13, alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: 6 }}>
                <Plus size={14} /> Add question
              </button>
            </div>
          </div>
        ))}
      </div>

      <button type="button" className="btn-secondary" onClick={addSection} data-testid={`intake-${formKey}-add-section`} style={{ marginTop: 16, display: "inline-flex", alignItems: "center", gap: 6 }}>
        <Plus size={14} /> Add section
      </button>
    </div>
  );
}
